// Espace employé — « Mon évaluation » : the employee side of the
// annual campaign (auto-évaluation, comparaison des écarts, objectifs,
// 1:1 & signature). Ported from the design prototype's employee
// persona (ev-shell.jsx views moneval/comparaison/mesobjectifs/monoto),
// fed by /api/me/evaluation.
import { useEffect, useState } from 'react';
import {
  useChooseMyOtoSlot, useCheckMyObjective, useMyEvaluation, useProposeMyObjective,
  useSaveMyAutoEval, useSignMyEval, useSubmitMyAutoEval,
  type MeOverview,
} from '../../api/hooks.js';
import { toast } from '../../store/toast.js';
import type { EvalEmployeeDTO } from '@pointage/shared';
import { EvEvalForm, evFmtYmd } from '../evaluations/common.js';
import { EvGapView, EvOtoView, EvFiche } from '../evaluations/GapOto.js';
import { EvObjSection, type ObjCheckInput } from '../evaluations/Objectifs.js';

type SubView = 'moneval' | 'comparaison' | 'mesobjectifs' | 'monoto';

export function esEvalBadge(evData: ReturnType<typeof useMyEvaluation>['data']): number {
  if (!evData) return 0;
  const ev = evData.evaluation;
  let n = 0;
  // A 1:1 slot to pick.
  if (ev.oto.stage === 'PROPOSED' && !ev.oto.chosen && ev.auto.status === 'SUBMITTED' && ev.mgr.status === 'SUBMITTED') n++;
  // A consensus sheet awaiting my signature.
  if (ev.final.cr && !ev.final.signedEmpAt) n++;
  // My auto-eval not submitted yet.
  if (ev.auto.status !== 'SUBMITTED') n++;
  return n;
}

export function EsEvaluation({ overview, year }: { overview: MeOverview; year: number }) {
  const q = useMyEvaluation(year);
  const [view, setView] = useState<SubView>('moneval');
  const [printing, setPrinting] = useState(false);

  const save = useSaveMyAutoEval();
  const submit = useSubmitMyAutoEval();
  const choose = useChooseMyOtoSlot();
  const sign = useSignMyEval();
  const proposeObj = useProposeMyObjective();
  const checkObj = useCheckMyObjective();

  useEffect(() => {
    if (!printing) return;
    const id = setTimeout(() => window.print(), 350);
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    return () => { clearTimeout(id); window.removeEventListener('afterprint', done); };
  }, [printing]);

  if (q.isLoading) return <div className="cg-placeholder" style={{ marginTop: 20 }}>Chargement de votre évaluation…</div>;
  if (!q.data) return <div className="cg-placeholder" style={{ marginTop: 20 }}>Impossible de charger votre évaluation. Rechargez la page.</div>;

  const { evaluation: ev, cycle, objectives } = q.data;
  const mgrName = q.data.managerName ?? 'Direction';
  const emp: EvalEmployeeDTO & { hiredAt?: string | null } = {
    id: overview.employee.id,
    code: overview.employee.code,
    name: overview.employee.name,
    entityId: overview.employee.entity.id,
    department: overview.employee.department,
    email: overview.employee.email,
    hiredAt: overview.employee.hiredAt,
  };

  const otoBadge = ev.oto.stage === 'PROPOSED' && !ev.oto.chosen && ev.auto.status === 'SUBMITTED' && ev.mgr.status === 'SUBMITTED' ? 1
    : ev.final.cr && !ev.final.signedEmpAt ? 1 : 0;

  const VIEWS: Array<[SubView, string, number]> = [
    ['moneval', 'Mon évaluation', ev.auto.status !== 'SUBMITTED' ? 1 : 0],
    ['comparaison', 'Comparaison', 0],
    ['mesobjectifs', 'Mes objectifs', objectives.filter((o) => o.status === 'PROPOSE').length],
    ['monoto', 'Mon 1:1', otoBadge],
  ];

  const err = (label: string) => () => toast.danger(label);

  return (
    <div>
      <div className="es-pagehead">
        <div>
          <div className="gm-eyebrow">Talent · {cycle.lbl}</div>
          <h1 className="gm-title sm">Mon évaluation <em>annuelle</em></h1>
          <p className="gm-sub">
            Complétez votre auto-évaluation, comparez-la avec celle de votre manager, choisissez votre créneau
            de 1:1 et suivez vos objectifs trimestre par trimestre.
          </p>
        </div>
      </div>

      <div className="cg-ctrl" style={{ marginBottom: 18 }}>
        <div className="cg-seg">
          {VIEWS.map(([k, l, badge]) => (
            <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k)}>
              {l}
              {badge > 0 && <span className="bdg">{badge}</span>}
            </button>
          ))}
        </div>
        <div className="cg-spacer" />
        <span className="ev-due warn" style={{ fontSize: 11 }}>Auto-éval : échéance {evFmtYmd(cycle.dAuto)}</span>
      </div>

      {view === 'moneval' && (
        <div className="view-enter">
          {ev.mgr.status === 'SUBMITTED' && ev.auto.status !== 'SUBMITTED' && (
            <div className="cg-rule">
              <span className="cg-rule-ic">i</span>
              <span>
                <b>Votre manager a déjà soumis son évaluation.</b> Dès que vous soumettez la vôtre, la comparaison
                des écarts s'ouvre pour vous deux et le 1:1 peut être planifié.
              </span>
            </div>
          )}
          <EvEvalForm key={`auto-${ev.auto.status}`} role="auto" tplCode={ev.template}
            notes={ev.auto.notes ?? {}} bilan={ev.auto.bilan ?? []}
            readOnly={ev.auto.status === 'SUBMITTED'} submittedAt={ev.auto.submittedAt}
            deadline={cycle.dAuto} saving={save.isPending}
            onSave={(d) => save.mutate({ year, notes: d.notes, bilan: d.bilan })}
            onSubmit={() => submit.mutate({ year }, {
              onSuccess: (dto) => toast.success(dto.mgr.status === 'SUBMITTED'
                ? 'Auto-évaluation soumise — la comparaison des écarts est maintenant disponible'
                : "Auto-évaluation soumise — en attente de l'évaluation de votre manager"),
              onError: err('Soumission impossible — vérifiez que tous les critères sont notés.'),
            })} />
        </div>
      )}

      {view === 'comparaison' && (
        <div className="view-enter">
          <EvGapView ev={ev} mgrName={mgrName}
            autoMissing="Vous n'avez pas encore soumis votre auto-évaluation." />
        </div>
      )}

      {view === 'mesobjectifs' && (
        <div className="view-enter">
          <EvObjSection objs={objectives} persona="emp" empName={overview.employee.name} mgrName={mgrName}
            onCreate={(tpl) => proposeObj.mutate({ year, ...tpl }, {
              onSuccess: () => toast.success(`Proposition envoyée à ${mgrName} pour validation`),
              onError: err("Impossible d'envoyer la proposition."),
            })}
            onCheck={(id: string, input: ObjCheckInput) => checkObj.mutate({ id, q: input.q, prog: input.prog, status: input.status, note: input.note }, {
              onSuccess: () => toast.success(`Check-in ${input.q} enregistré`),
              onError: err('Check-in impossible.'),
            })} />
        </div>
      )}

      {view === 'monoto' && (
        <div className="view-enter">
          <EvOtoView emp={emp} ev={ev} cycle={cycle} persona="emp" mgrName={mgrName}
            busy={choose.isPending || sign.isPending}
            actions={{
              choose: (slot) => choose.mutate({ year, d: slot.d, h: slot.h }, {
                onSuccess: () => toast.success(`1:1 confirmé — ${slot.d.split('-').reverse().slice(0, 2).join('/')} à ${slot.h}`),
                onError: err('Impossible de confirmer ce créneau.'),
              }),
              signEmp: () => sign.mutate({ year }, {
                onSuccess: (dto) => toast.success(dto.final.done
                  ? 'Fiche signée par les deux parties — évaluation clôturée 🎉'
                  : "Signature enregistrée — en attente de la signature du manager"),
                onError: err('Signature impossible.'),
              }),
              print: () => setPrinting(true),
            }} />
        </div>
      )}

      {printing && <EvFiche emp={emp} ev={ev} objs={objectives} mgrName={mgrName} cycle={cycle} />}
    </div>
  );
}
